"use client";

/**
 * Hook for managing betting history list with pagination and infinite scroll
 */

import type { TPagination } from "@/shared/types";
import { TFilters } from "@/shared/ui/filters/types";
import { OPEN_BET_LIFECYCLES } from "@/types/index";
import { tradingClient } from "@/shared/rpc/clients";
import { parseEnumListFilter } from "@/utils/parse-enum-list-filter";
import type { BetMessage } from "@cronika/proto/common/trading_pb";
import { BetLifecycle } from "@cronika/proto/common/trading_pb";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { DEFAULT_SIZE, DEFAULT_REQUEST_PARAMS, INITIAL_PAGINATION } from "./constants";
import { TUseHistoryList } from "./types";

type TListBetsParams = Record<string, unknown>;

export const useHistoryList: TUseHistoryList = (defaultParams) => {
  const [searchParams] = useSearchParams();

  const urlFilters: TFilters = useMemo(() => {
    const filters: TFilters = {};
    searchParams.forEach((value, key) => {
      filters[key] = value;
    });
    return filters;
  }, [searchParams]);

  // State
  const [list, setList] = useState<BetMessage[]>([]);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [pagination, setPagination] = useState<TPagination>(INITIAL_PAGINATION);
  const [fetching, setFetching] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastRequestedPage, setLastRequestedPage] = useState<number | null>(null);

  // Computed
  const hasMore = list.length < pagination.total;

  /**
   * Fetch betting history list
   */
  const getList = useCallback(
    async (params?: TListBetsParams): Promise<void> => {
      try {
        setFetching(true);

        const page = urlFilters.page ? Number(urlFilters.page) : 1;

        const { page: _page, ...restUrlFilters } = urlFilters;
        const urlFiltersClean: TFilters = {
          ...restUrlFilters,
          ...(urlFilters.createdAtFrom && {
            createdAtFrom: new Date(urlFilters.createdAtFrom).toISOString(),
          }),
          ...(urlFilters.settledAtTo && {
            settledAtTo: new Date(urlFilters.settledAtTo).toISOString(),
          }),
        };
        if (defaultParams?.lifecycle) {
          delete urlFiltersClean.lifecycle;
          delete urlFiltersClean.status;
        }

        // Build lifecycle array
        let lifecycle: BetLifecycle[] = [...DEFAULT_REQUEST_PARAMS.lifecycle];
        const rawLifecycle = params?.lifecycle ?? urlFiltersClean.lifecycle ?? defaultParams?.lifecycle;
        if (rawLifecycle === "open") {
          lifecycle = [...OPEN_BET_LIFECYCLES];
        } else if (rawLifecycle != null) {
          lifecycle = Array.isArray(rawLifecycle)
            ? (rawLifecycle as unknown as BetLifecycle[])
            : [rawLifecycle as unknown as BetLifecycle];
        }
        delete urlFiltersClean.lifecycle;

        // Resolve betType filter → number array (URL value is comma-separated, e.g. "1,2")
        const betType = parseEnumListFilter(urlFiltersClean.betType);

        // Resolve outcome filter → number array (URL value is comma-separated, e.g. "5,6")
        const outcome = parseEnumListFilter(urlFiltersClean.outcome);

        const requestParams: TListBetsParams = params ?? {
          sortBy: DEFAULT_REQUEST_PARAMS.sortBy,
          sortDir: DEFAULT_REQUEST_PARAMS.sortDir,
          lifecycle,
          pagination: { page, size },
          ...(urlFiltersClean.pairId && { pairId: urlFiltersClean.pairId }),
          ...(urlFiltersClean.createdAtFrom && { createdAtFrom: urlFiltersClean.createdAtFrom }),
          ...(urlFiltersClean.settledAtTo && { settledAtTo: urlFiltersClean.settledAtTo }),
          ...(betType?.length && { betType }),
          ...(outcome?.length && { outcome }),
          ...defaultParams,
        };

        if (params && !params.lifecycle) {
          requestParams.lifecycle = lifecycle;
        }
        if (!requestParams.pagination) {
          requestParams.pagination = { page, size };
        }

        const response = await tradingClient.listBets(requestParams as any);

        const pg = response.pagination;
        setList(response.data);
        setPagination({
          page: pg?.page ?? page,
          size: pg?.size ?? size,
          total: pg?.total ?? 0,
        });
        setLastRequestedPage(null);
      } catch {
        // error handled silently
      } finally {
        setFetching(false);
      }
    },
    [size, urlFilters, defaultParams],
  );

  /**
   * Load more items for infinite scroll
   */
  const loadMore = useCallback(async (): Promise<void> => {
    if (!hasMore || loadingMore) return;

    const nextPage = Math.floor(list.length / size) + 1;

    // Prevent duplicate requests
    if (lastRequestedPage === nextPage) return;

    try {
      setLoadingMore(true);
      setLastRequestedPage(nextPage);

      // Build lifecycle
      let lifecycle: BetLifecycle[] = [...DEFAULT_REQUEST_PARAMS.lifecycle];
      const rawLifecycle = defaultParams?.lifecycle;
      if (rawLifecycle) {
        lifecycle = Array.isArray(rawLifecycle) ? [...rawLifecycle] : [rawLifecycle];
      }

      const requestParams: TListBetsParams = {
        sortBy: DEFAULT_REQUEST_PARAMS.sortBy,
        sortDir: DEFAULT_REQUEST_PARAMS.sortDir,
        lifecycle,
        pagination: { page: nextPage, size },
        ...defaultParams,
      };

      const response = await tradingClient.listBets(requestParams as any);

      const pg = response.pagination;
      setList((prevList) => [...prevList, ...response.data]);
      setPagination({
        page: pg?.page ?? nextPage,
        size: pg?.size ?? size,
        total: pg?.total ?? 0,
      });
    } catch {
      setLastRequestedPage(null);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, list.length, lastRequestedPage, size, urlFilters, defaultParams]);

  // Fetch when URL filters, size, or defaultParams change
  useEffect(() => {
    getList(defaultParams as TListBetsParams);
  }, [
    urlFilters.page,
    urlFilters.betType,
    urlFilters.pairId,
    urlFilters.settledAtFrom,
    urlFilters.createdAtFrom,
    urlFilters.accountType,
    urlFilters.outcome,
    urlFilters.lifecycle,
    size,
    defaultParams?.outcome,
    defaultParams?.lifecycle,
    defaultParams?.accountType,
  ]);

  /**
   * Add item to the beginning of the list without refetching
   */
  const prependItem = useCallback((item: BetMessage) => {
    setList((prev) => {
      if (prev.some((b) => b.id === item.id)) return prev;
      return [item, ...prev];
    });
    setPagination((prev) => ({ ...prev, total: prev.total + 1 }));
  }, []);

  /**
   * Remove item from list by id
   */
  const removeItem = useCallback((id: string) => {
    setList((prev) => prev.filter((b) => b.id !== id));
    setPagination((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  }, []);

  return {
    list,
    fetching,
    loadingMore,
    pagination,
    size,
    hasMore,
    getList,
    setSize,
    loadMore,
    prependItem,
    removeItem,
  };
};
